import type { LlmGenerationRunTaskRequest } from "../../protocol"
import { getActiveClerkToken } from "./activeClerkToken"
import {
  isSofiaLlmGenerationTaskId,
  runSofiaLlmGenerationJob,
  SofiaLlmGenerationJobClientError,
} from "./sofiaJobs"

interface RunLlmGenerationTaskOptions {
  endpoint?: string
  signal?: AbortSignal
}

interface LlmGenerationRunTaskErrorResponse {
  error?: string
}

const DEFAULT_SOFIA_JOB_ERROR_STATUS = 500

export class LlmGenerationRunTaskClientError extends Error {
  readonly runId?: string
  readonly status: number

  constructor(input: { message: string; runId?: string; status: number }) {
    super(input.message)
    this.name = "LlmGenerationRunTaskClientError"
    this.runId = input.runId
    this.status = input.status
  }
}

async function getRunTaskErrorMessage(response: Response): Promise<string> {
  try {
    const parsed = (await response.json()) as LlmGenerationRunTaskErrorResponse
    return parsed.error || `LLM generation request failed (${response.status})`
  } catch {
    return `LLM generation request failed (${response.status})`
  }
}

export async function runLlmGenerationTask<TOutput = unknown>(
  request: LlmGenerationRunTaskRequest,
  options: RunLlmGenerationTaskOptions = {},
): Promise<TOutput> {
  if (!options.endpoint) {
    if (!isSofiaLlmGenerationTaskId(request.taskId)) {
      throw new LlmGenerationRunTaskClientError({
        message: `Unsupported LLM generation task: ${request.taskId}`,
        status: 400,
      })
    }

    try {
      return await runSofiaLlmGenerationJob<TOutput>(request, {
        signal: options.signal,
      })
    } catch (error: unknown) {
      if (!(error instanceof SofiaLlmGenerationJobClientError)) {
        throw error
      }
      throw new LlmGenerationRunTaskClientError({
        message: error.message,
        runId: error.runId,
        status: error.status ?? DEFAULT_SOFIA_JOB_ERROR_STATUS,
      })
    }
  }

  const token = await getActiveClerkToken()
  if (!token) {
    throw new LlmGenerationRunTaskClientError({
      message: "Organization authentication token is unavailable",
      status: 401,
    })
  }
  const response = await fetch(options.endpoint, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
    signal: options.signal,
  })

  if (!response.ok) {
    throw new LlmGenerationRunTaskClientError({
      message: await getRunTaskErrorMessage(response),
      status: response.status,
    })
  }

  return (await response.json()) as TOutput
}
